<template>
  <div class="min-h-screen pb-10">
    <PageTitle title="Factures Proforma" :description="`${total} facture(s) proforma au total`">
      <template #actions>
        <BaseButton variant="primary" @click="handleCreateProforma">
          <template #icon-left>
            <i class="fa-solid fa-plus"></i>
          </template>
          Nouvelle proforma
        </BaseButton>
      </template>
    </PageTitle>

    <!-- Filtres -->
    <BaseCard class="mb-6">
      <div class="flex flex-wrap gap-4">
        <!-- Recherche -->
        <div class="flex-1 min-w-[250px]">
          <BaseInput 
            v-model="search" 
            type="search" 
            placeholder="Rechercher une proforma (numéro, client)..."
            icon-left='<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>'
            clearable 
            @input="handleSearch" 
          />
        </div>

        <!-- Filtres de date -->
        <div class="flex gap-3">
          <div class="flex flex-col gap-1">
            <label for="dateFrom" class="text-sm font-medium text-gray-600">Du</label>
            <BaseInput id="dateFrom" v-model="filters.dateFrom" type="date" @change="loadProformaInvoices" />
          </div>
          <div class="flex flex-col gap-1">
            <label for="dateTo" class="text-sm font-medium text-gray-600">Au</label>
            <BaseInput id="dateTo" v-model="filters.dateTo" type="date" @change="loadProformaInvoices" />
          </div>
        </div>

        <!-- Filtre de statut -->
        <div class="flex flex-col gap-1">
          <label for="statusFilter" class="text-sm font-medium text-gray-600">Statut</label>
          <select 
            id="statusFilter" 
            v-model="filters.status" 
            @change="loadProformaInvoices"
            class="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-[180px]"
          >
            <option value="">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="accepted">Acceptée</option>
            <option value="rejected">Rejetée</option>
          </select>
        </div>
      </div>
    </BaseCard>

    <!-- Tableau des factures proforma -->
    <BaseCard>
      <!-- État de chargement -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <div class="w-10 h-10 border-4 border-gray-200 border-t-primary rounded-full animate-spin mb-4"></div>
        <p class="text-gray-600">Chargement des factures proforma...</p>
      </div>

      <!-- État vide -->
      <div v-else-if="proformaInvoices.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 text-gray-300 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Aucune facture proforma trouvée</h3>
        <p class="text-gray-600 max-w-md mb-6">
          Cliquez sur "Nouvelle proforma" pour créer votre première facture proforma.
        </p>
        <BaseButton variant="primary" size="lg" @click="handleCreateProforma">
          <template #icon-left>
            <i class="fa-solid fa-plus"></i>
          </template>
          Créer une proforma
        </BaseButton>
      </div>

      <!-- Tableau -->
      <div v-else class="overflow-x-auto min-h-[500px]">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-50 border-b-2 border-gray-200">
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                N° Proforma
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Date
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Date de validité
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Client
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Montant
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Statut
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Créé par
              </th>
              <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="proforma in proformaInvoices" 
              :key="proforma.id" 
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="px-4 py-4">
                <span class="font-mono font-semibold text-gray-900">{{ proforma.proforma_number }}</span>
              </td>
              <td class="px-4 py-4 text-sm text-gray-700">
                {{ formatDate(proforma.proforma_date) }}
              </td>
              <td class="px-4 py-4 text-sm text-gray-700">
                <span v-if="proforma.validity_date">{{ formatDate(proforma.validity_date) }}</span>
                <span v-else class="text-gray-400 italic">-</span>
              </td>
              <td class="px-4 py-4 text-sm text-gray-700">
                <span v-if="proforma.customer && proforma.customer.name" class="text-gray-900 font-medium">
                  {{ proforma.customer.name }}
                </span>
                <span v-else class="text-gray-400 italic">-</span>
              </td>
              <td class="px-4 py-4">
                <span class="font-semibold text-gray-900">{{ formatCurrency(proforma.total_incl_tax) }}</span>
              </td>
              <td class="px-4 py-4">
                <BaseBadge 
                  :variant="getStatusVariant(proforma.status)" 
                  :text="getStatusLabel(proforma.status)"
                  size="sm"
                />
              </td>
              <td class="px-4 py-4 text-sm text-gray-700">
                {{ proforma.user ? proforma.user.name : 'N/A' }}
              </td>
              <td class="px-4 py-4">
                <div class="flex items-center justify-end">
                  <BaseDropdown position="bottom-right" width="md">
                    <template #trigger>
                      <button
                        type="button"
                        class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <i class="fa-solid fa-ellipsis-vertical"></i>
                      </button>
                    </template>

                    <button
                      type="button"
                      class="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      @click="handleViewDetails(proforma)"
                    >
                      <i class="fa-solid fa-eye mr-3 text-gray-400"></i>
                      Voir les détails
                    </button>

                    <button
                      type="button"
                      class="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      :class="{ 'opacity-50 cursor-not-allowed': isDownloading || proforma.loading }"
                      :disabled="isDownloading || proforma.loading"
                      @click="handleDownloadPdf(proforma)"
                    >
                      <i class="fa-solid fa-file-pdf mr-3 text-gray-400"></i>
                      {{ proforma.loading ? 'Téléchargement...' : 'Télécharger PDF' }}
                    </button>

                    <hr class="my-1 border-gray-200" />

                    <button
                      type="button"
                      class="w-full flex items-center px-4 py-2 text-sm transition-colors"
                      :class="canConvert(proforma) ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 cursor-not-allowed'"
                      :disabled="!canConvert(proforma)"
                      @click="canConvert(proforma) && handleConvertToSale(proforma)"
                    >
                      <i class="fa-solid fa-exchange-alt mr-3" :class="canConvert(proforma) ? 'text-gray-400' : 'text-gray-300'"></i>
                      Convertir en vente
                    </button>

                    <button
                      type="button"
                      class="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      @click="handleDuplicate(proforma)"
                    >
                      <i class="fa-solid fa-copy mr-3 text-gray-400"></i>
                      Dupliquer
                    </button>

                    <button
                      type="button"
                      class="w-full flex items-center px-4 py-2 text-sm transition-colors"
                      :class="canEdit(proforma) ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 cursor-not-allowed'"
                      :disabled="!canEdit(proforma)"
                      @click="canEdit(proforma) && handleEdit(proforma)"
                    >
                      <i class="fa-solid fa-edit mr-3" :class="canEdit(proforma) ? 'text-gray-400' : 'text-gray-300'"></i>
                      Modifier
                    </button>
                  </BaseDropdown>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="proformaInvoices.length > 0">
        <PaginationComponent 
          :current-page="currentPage" 
          :per-page="perPage" 
          :total="total" 
          :to="paginationInfo.to"
          :from="paginationInfo.from"
          :show-page-numbers="true" 
          :max-visible-pages="5" 
          @page-change="goToPage" 
        />
      </div>
    </BaseCard>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { proformaInvoiceService } from '@/services'
import PageTitle from '@/components/common/PageTitle.vue'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'

export default defineComponent({
  name: 'ProformaInvoicesView',
  components: {
    PageTitle,
    BaseCard,
    BaseButton,
    BaseInput,
    BaseBadge,
    BaseDropdown,
    PaginationComponent
  },
  setup() {
    const router = useRouter()
    
    // Données d'état
    const proformaInvoices = ref([])
    const loading = ref(true)
    const isDownloading = ref(false)
    const search = ref('')
    const filters = reactive({
      dateFrom: '',
      dateTo: '',
      status: ''
    })

    // Pagination
    const currentPage = ref(1)
    const lastPage = ref(1)
    const perPage = ref(15)
    const total = ref(0)
    const paginationTo = ref(0)
    const paginationFrom = ref(0)
    const searchTimeout = ref(null)

    // Calcul des informations de pagination
    const paginationInfo = computed(() => {
      return {
        from: paginationFrom.value,
        to: paginationTo.value,
        total: total.value
      }
    })

    // Méthodes
    const loadProformaInvoices = async () => {
      try {
        loading.value = true

        // Préparer les paramètres
        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          with: ['customer', 'user', 'sale']
        }

        // Ajouter les filtres
        if (search.value) {
          params.search = search.value
        }
        if (filters.dateFrom) {
          params.date_from = filters.dateFrom
        }
        if (filters.dateTo) {
          params.date_to = filters.dateTo
        }
        if (filters.status) {
          params.status = filters.status
        }

        // Appel à l'API
        const response = await proformaInvoiceService.getProformaInvoices(params)

        // Structure Laravel: { status: 'success', data: { data: [...], meta: {...} } }
        // ou directement: { data: [...], meta: {...} }
        const responseData = response.data?.data || response.data || {}
        proformaInvoices.value = responseData.data || responseData || []
        
        // Initialiser l'état de chargement pour chaque proforma
        proformaInvoices.value.forEach(proforma => {
          proforma.loading = false
        })

        // Mettre à jour les données de pagination depuis meta
        const meta = responseData.meta || response.data?.meta || response.meta || {}
        total.value = meta.total || 0
        perPage.value = meta.per_page || 15
        paginationTo.value = meta.to || 0
        paginationFrom.value = meta.from || 0
        lastPage.value = Math.ceil(total.value / perPage.value) || 1

      } catch (error) {
        console.error('Erreur lors du chargement des factures proforma:', error)
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      currentPage.value = 1
      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(() => {
        loadProformaInvoices()
      }, 500)
    }

    const goToPage = (page) => {
      if (page < 1 || page > lastPage.value) return
      currentPage.value = page
      loadProformaInvoices()
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''

      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const getStatusLabel = (status) => {
      return proformaInvoiceService.getStatusLabel(status)
    }

    const getStatusVariant = (status) => {
      const statusVariants = {
        'draft': 'gray',
        'accepted': 'success',
        'rejected': 'danger'
      }
      return statusVariants[status] || 'gray'
    }

    const canEdit = (proforma) => {
      return proformaInvoiceService.canBeModified(proforma)
    }

    const canConvert = (proforma) => {
      return proformaInvoiceService.canBeConverted(proforma)
    }

    // Actions
    const handleCreateProforma = () => {
      router.push({ name: 'create-proforma-invoice' })
    }

    const handleViewDetails = (proforma) => {
      console.log('Voir les détails de la proforma:', proforma)
    }

    const handleDownloadPdf = async (proforma) => {
      if (proforma.loading || isDownloading.value) return
      
      proforma.loading = true
      isDownloading.value = true
      
      try {
        await proformaInvoiceService.downloadPdf(proforma.id, true)
      } catch (error) {
        console.error('Erreur lors du téléchargement du PDF:', error)
        alert('Erreur lors du téléchargement du PDF. Veuillez réessayer.')
      } finally {
        proforma.loading = false
        isDownloading.value = false
      }
    }

    const handleConvertToSale = (proforma) => {
      console.log('Convertir la proforma en vente:', proforma)
    }

    const handleDuplicate = (proforma) => {
      console.log('Dupliquer la proforma:', proforma)
    }

    const handleEdit = (proforma) => {
      console.log('Modifier la proforma:', proforma)
    }

    // Nettoyer le timeout lors du démontage
    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
    })

    // Charger les données au montage du composant
    onMounted(() => {
      loadProformaInvoices()
    })

    return {
      proformaInvoices,
      loading,
      search,
      filters,
      currentPage,
      lastPage,
      perPage,
      total,
      paginationInfo,
      loadProformaInvoices,
      handleSearch,
      goToPage,
      formatCurrency,
      formatDate,
      getStatusLabel,
      getStatusVariant,
      canEdit,
      canConvert,
      handleCreateProforma,
      handleViewDetails,
      handleDownloadPdf,
      handleConvertToSale,
      handleDuplicate,
      handleEdit,
      router,
      isDownloading
    }
  }
})
</script>

<style scoped>
</style>
